import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { ArrowRight, Flame, BookOpen, Compass, Mic, Radar, PenLine, CalendarCheck } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { InstallAppButton } from "@/components/site/InstallAppButton";
import { LeadMagnetForm } from "@/components/site/LeadMagnetForm";
import { MAKECHANGE_PILLARS, type PillarKey } from "@/content/makechange";
import { PILLAR_DAILY, focusFromScores } from "@/content/pillar-daily";
import { BLOG_POSTS } from "@/content/blog";
import { cn } from "@/lib/utils";

const KEY = "mcn-checkins-v1";
const SCAN_KEY = "mc_assessment_v1";
type Entry = { score: number; note: string; reflection?: string; actionDone?: boolean; coach?: string };
type Checkins = Record<string, Entry>;
const ORDER = MAKECHANGE_PILLARS.map((p) => p.key) as PillarKey[];
const COACH_URL = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/app-coach`;

const today = () => new Date().toISOString().slice(0, 10);
const load = (): Checkins => {
  try { return JSON.parse(localStorage.getItem(KEY) || "{}"); } catch { return {}; }
};
const loadScan = (): Partial<Record<PillarKey, number>> | null => {
  try {
    const p = JSON.parse(localStorage.getItem(SCAN_KEY) || "null");
    return p?.done && p.scores ? p.scores : null;
  } catch { return null; }
};
const dayIndex = () => Math.floor(Date.now() / 86_400_000);

const streakOf = (c: Checkins) => {
  let n = 0;
  const d = new Date();
  if (!c[today()]) d.setDate(d.getDate() - 1);
  while (c[d.toISOString().slice(0, 10)]) { n++; d.setDate(d.getDate() - 1); }
  return n;
};

const TOOLS = [
  { to: "/scan", label: "Opportunity Scan", icon: Radar },
  { to: "/framework", label: "Pillar Assessment", icon: Compass },
  { to: "/books", label: "Books", icon: BookOpen },
  { to: "/blog", label: "Insights", icon: PenLine },
  { to: "/honest-kingdom-voice", label: "Honest Kingdom Voice", icon: Mic },
  { to: "/inquire", label: "Work With Dr. Sully", icon: CalendarCheck },
];

const AppHome = () => {
  const [checkins, setCheckins] = useState<Checkins>(load);
  const [user, setUser] = useState<{ id: string; email?: string } | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [scan, setScan] = useState(loadScan);
  const focus = useMemo(() => (scan ? focusFromScores(scan, ORDER) : []), [scan]);
  const pillar = focus.length
    ? MAKECHANGE_PILLARS.find((p) => p.key === focus[dayIndex() % focus.length])!
    : MAKECHANGE_PILLARS[dayIndex() % MAKECHANGE_PILLARS.length];
  const daily = PILLAR_DAILY[pillar.key];
  const done = checkins[today()];
  const [score, setScore] = useState(done?.score ?? 0);
  const [note, setNote] = useState(done?.note ?? "");
  const [reflection, setReflection] = useState(done?.reflection ?? "");
  const [actionDone, setActionDone] = useState(done?.actionDone ?? false);
  const [coach, setCoach] = useState(done?.coach ?? "");
  const [coaching, setCoaching] = useState(false);
  const [coachError, setCoachError] = useState<string | null>(null);
  const streak = streakOf(checkins);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setUser(s?.user ? { id: s.user.id, email: s.user.email } : null));
    supabase.auth.getUser().then(({ data }) => setUser(data.user ? { id: data.user.id, email: data.user.email } : null));
    return () => sub.subscription.unsubscribe();
  }, []);

  // On sign-in: upload on-phone check-ins + scan, then load the saved history.
  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    (async () => {
      const local = load();
      const rows = Object.entries(local).map(([day, v]) => ({
        user_id: user.id, day, score: v.score, note: v.note, reflection: v.reflection ?? null, action_done: !!v.actionDone,
      }));
      if (rows.length) await supabase.from("app_checkins").upsert(rows, { onConflict: "user_id,day", ignoreDuplicates: true });
      const { data } = await supabase.from("app_checkins")
        .select("day,score,note,reflection,action_done,coach_reply")
        .eq("user_id", user.id).order("day", { ascending: false }).limit(400);
      const merged: Checkins = { ...local };
      (data ?? []).forEach((r) => {
        merged[r.day] = { score: r.score, note: r.note ?? "", reflection: r.reflection ?? "", actionDone: r.action_done, coach: r.coach_reply ?? "" };
      });
      localStorage.setItem(KEY, JSON.stringify(merged));
      setCheckins(merged);
      const t = merged[today()];
      if (t) { setScore(t.score); setNote(t.note); setReflection(t.reflection ?? ""); setActionDone(!!t.actionDone); setCoach(t.coach ?? ""); }

      const localScan = loadScan();
      if (localScan) {
        await supabase.from("app_pillar_scans").upsert({ user_id: user.id, scores: localScan, focus: focusFromScores(localScan, ORDER) });
      } else {
        const { data: s } = await supabase.from("app_pillar_scans").select("scores").eq("user_id", user.id).maybeSingle();
        if (s?.scores) setScan(s.scores as Partial<Record<PillarKey, number>>);
      }

      const { data: role } = await supabase.from("user_roles").select("role").eq("user_id", user.id).eq("role", "admin").maybeSingle();
      setIsAdmin(!!role);
    })();
  }, [user]);
  const latest = useMemo(
    () => [...BLOG_POSTS].sort((a, b) => b.publishedISO.localeCompare(a.publishedISO)).slice(0, 3),
    [],
  );
  const last7 = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (6 - i));
    const k = d.toISOString().slice(0, 10);
    return { k, s: checkins[k]?.score ?? 0 };
  });

  const persist = (next: Checkins) => { localStorage.setItem(KEY, JSON.stringify(next)); setCheckins(next); };

  const askCoach = async (day: string, base: Checkins) => {
    setCoaching(true); setCoachError(null); setCoach("");
    try {
      const { data: s } = await supabase.auth.getSession();
      const res = await fetch(COACH_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${s.session?.access_token ?? ""}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ day }),
      });
      let text = "";
      if (res.headers.get("Content-Type")?.includes("application/json")) {
        const j = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(j.error ?? "The coach is unavailable right now.");
        text = j.reply ?? "";
        setCoach(text);
      } else {
        if (!res.ok || !res.body) throw new Error("The coach is unavailable right now.");
        const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
        for (;;) {
          const { value, done: end } = await reader.read();
          if (end) break;
          buf += dec.decode(value, { stream: true });
          const lines = buf.split("\n"); buf = lines.pop() ?? "";
          for (const l of lines) {
            if (!l.startsWith("data:")) continue;
            try {
              const ev = JSON.parse(l.slice(5).trim());
              if (ev.type === "response.output_text.delta") { text += ev.delta; setCoach(text); }
              else if (ev.type === "response.failed" || ev.type === "error") throw new Error("The coach couldn't finish a reply.");
            } catch (e) { if (e instanceof Error && !(e instanceof SyntaxError)) throw e; }
          }
        }
      }
      if (text) persist({ ...base, [day]: { ...base[day], coach: text } });
    } catch (e) {
      setCoachError(e instanceof Error ? e.message : "The coach is unavailable right now.");
    } finally { setCoaching(false); }
  };

  const save = async () => {
    if (!score) return;
    const day = today();
    const entry: Entry = { score, note: note.slice(0, 500), reflection: reflection.slice(0, 1000), actionDone, coach: checkins[day]?.coach };
    const next = { ...checkins, [day]: entry };
    persist(next);
    if (user) {
      const { error } = await supabase.from("app_checkins").upsert(
        { user_id: user.id, day, pillar: pillar.name, score: entry.score, note: entry.note, reflection: entry.reflection, action_done: actionDone },
        { onConflict: "user_id,day" });
      setSaved(error ? "Saved on this phone only — couldn't reach your account." : "Saved to your account.");
      if (!error && !entry.coach) void askCoach(day, next);
    } else setSaved("Saved on this phone. Sign in to keep it safe on every device.");
  };

  const signOut = () => {
    supabase.auth.signOut(); localStorage.removeItem(KEY); setCheckins({});
    setScore(0); setNote(""); setReflection(""); setActionDone(false); setCoach("");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title="MAKE CHANGE NOW App | Dr. Sully"
        description="Install the MAKE CHANGE NOW app: a daily MAKECHANGE pillar, streak check-ins, new insights, and Dr. Sully's tools in your pocket."
        path="/app"
      />
      <SiteNav />
      <main id="main">
        <section className="pt-36 md:pt-44 pb-12 bg-hero">
          <div className="container max-w-4xl">
            <span className="eyebrow">The App</span>
            <h1 className="font-display font-bold uppercase text-white text-4xl md:text-6xl mt-4 leading-[1.05]">
              Make Change <span className="text-gradient-gold">Now</span>.
            </h1>
            <p className="text-silver text-lg mt-5 max-w-2xl">
              One pillar a day. One honest check-in. A streak that proves the change is real.
              Put Dr. Sully's MAKECHANGE system on your home screen.
            </p>
            <InstallAppButton className="mt-8" />
            <div className="mt-6 text-sm text-silver flex flex-wrap items-center gap-3">
              {user ? (
                <>
                  <span>Signed in as <span className="text-white">{user.email}</span> — check-ins save to your account.</span>
                  {isAdmin && <Link to="/admin/app-progress" className="text-gold underline">Follow everyone's progress</Link>}
                  <button type="button" onClick={signOut} className="underline hover:text-white">Sign out</button>
                </>
              ) : (
                <>
                  <span>Sign in to save your check-ins and streak on every device.</span>
                  <Link to="/auth?next=/app" className="border border-gold text-gold px-4 py-2 uppercase tracking-[0.2em] text-xs hover:bg-gold hover:text-navy">Sign in / Create account</Link>
                </>
              )}
            </div>
          </div>
        </section>

        {/* DAILY PILLAR */}
        <section className="py-12 border-t border-border">
          <div className="container max-w-4xl grid md:grid-cols-[1fr_260px] gap-8">
            <div className="border border-border bg-navy p-8">
              <div className="text-[10px] uppercase tracking-[0.3em] text-gold">Today's pillar</div>
              <div className="flex items-baseline gap-4 mt-3">
                <span className="font-display text-6xl text-gradient-gold">{pillar.letter}</span>
                <h2 className="font-display uppercase text-white text-3xl">{pillar.name}</h2>
              </div>
              <p className="text-silver mt-3">{pillar.desc}</p>
              <p className="text-[11px] text-silver/70 mt-2">
                {focus.length
                  ? <>From your pillar scan — your focus: {focus.map((k) => MAKECHANGE_PILLARS.find((p) => p.key === k)?.name).join(", ")}. <Link to="/framework" className="underline hover:text-white">Retake</Link></>
                  : <>Everyone's pillar today. <Link to="/framework" className="text-gold underline">Take the pillar scan</Link> and the app will focus on the pillars you need most.</>}
              </p>
              <div className="mt-6 text-silver leading-relaxed text-[15px]">{daily.teaching}</div>
              <p className="text-[11px] text-silver/60 mt-2 italic">Adapted from {daily.source} by Dr. Sully</p>

              <p className="text-white mt-8 text-sm uppercase tracking-[0.2em]">
                How strong was your {pillar.name.toLowerCase()} today?
              </p>
              <div className="flex gap-2 mt-3" role="radiogroup" aria-label="Rate today">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={score === n}
                    onClick={() => setScore(n)}
                    className={cn(
                      "h-12 w-12 border font-display text-lg transition-all",
                      score >= n ? "bg-gold text-navy border-gold" : "border-border text-silver hover:border-gold/60",
                    )}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <label className="block text-white mt-6 text-sm">
                <span className="text-[10px] uppercase tracking-[0.3em] text-gold">Reflect</span>
                <span className="block mt-1">{daily.reflection}</span>
                <textarea
                  value={reflection}
                  onChange={(e) => setReflection(e.target.value)}
                  maxLength={1000}
                  placeholder="Your honest answer…"
                  className="w-full mt-2 bg-navy-deep border border-border p-3 text-sm text-white placeholder:text-silver/50 focus:outline-none focus:border-gold/60"
                  rows={3}
                />
              </label>
              <label className="flex items-start gap-3 mt-5 cursor-pointer">
                <input type="checkbox" checked={actionDone} onChange={(e) => setActionDone(e.target.checked)} className="mt-1 h-4 w-4 accent-[hsl(var(--gold))]" />
                <span className="text-sm text-white">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-gold block">Today's action step</span>
                  {daily.action}
                </span>
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={500}
                placeholder="Anything else on your mind? (optional)"
                className="w-full mt-5 bg-navy-deep border border-border p-3 text-sm text-white placeholder:text-silver/50 focus:outline-none focus:border-gold/60"
                rows={2}
              />
              <button
                type="button"
                onClick={save}
                disabled={!score || coaching}
                className="mt-4 inline-flex items-center gap-2 border border-gold/50 text-white font-display uppercase tracking-[0.2em] text-xs px-6 py-3 hover:bg-gold/10 disabled:opacity-40 transition-all"
              >
                {done ? "Update check-in" : "Save check-in"}
              </button>
              {done && <p className="text-gold text-sm mt-3">Checked in today. Keep the momentum.</p>}
              {saved && <p className="text-silver text-xs mt-1" role="status">{saved}</p>}
              {(coach || coaching || coachError) && (
                <div className="mt-6 border-l-2 border-gold pl-4" aria-live="polite">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-gold">From your coach</div>
                  {coachError ? (
                    <p className="text-silver text-sm mt-2">{coachError}</p>
                  ) : (
                    <p className="text-white text-sm mt-2 whitespace-pre-wrap">{coach || "Reading your check-in…"}</p>
                  )}
                </div>
              )}
              {!user && done && (
                <p className="text-silver text-xs mt-4">
                  <Link to="/auth?next=/app" className="text-gold underline">Sign in</Link> to get a personal reply from your coach after each check-in.
                </p>
              )}
            </div>

            <div className="border border-border bg-navy p-6">
              <div className="flex items-center gap-2 text-gold">
                <Flame className="h-5 w-5" />
                <span className="font-display text-4xl text-white">{streak}</span>
              </div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-silver mt-1">day streak</p>
              <div className="flex items-end gap-1.5 h-24 mt-6" aria-label="Last 7 days">
                {last7.map((d) => (
                  <div key={d.k} className="flex-1 bg-navy-deep relative h-full" title={`${d.k}: ${d.s || "—"}`}>
                    <div className="absolute bottom-0 inset-x-0 bg-gold" style={{ height: `${d.s * 20}%` }} />
                  </div>
                ))}
              </div>
              <p className="text-xs text-silver/70 mt-3">Last 7 days · saved on this device</p>
            </div>
          </div>
        </section>

        {/* TOOLS */}
        <section className="py-12 border-t border-border">
          <div className="container max-w-4xl">
            <span className="eyebrow">Your toolkit</span>
            <ul className="grid grid-cols-2 md:grid-cols-3 gap-px bg-border border border-border mt-6">
              {TOOLS.map(({ to, label, icon: Icon }) => (
                <li key={to} className="bg-navy hover:bg-navy-elevated transition-colors">
                  <Link to={to} className="flex flex-col gap-3 p-6 group">
                    <Icon className="h-6 w-6 text-gold" />
                    <span className="font-display uppercase text-white text-sm tracking-wide">{label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* LATEST */}
        <section className="py-12 border-t border-border">
          <div className="container max-w-4xl">
            <span className="eyebrow">New from Dr. Sully</span>
            <ul className="mt-6 space-y-px bg-border border border-border">
              {latest.map((p) => (
                <li key={p.slug} className="bg-navy hover:bg-navy-elevated">
                  <Link to={`/blog/${p.slug}`} className="flex items-center justify-between gap-4 p-5 group">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.3em] text-gold">{p.eyebrow}</div>
                      <div className="font-display uppercase text-white mt-1">{p.title}</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-gold group-hover:translate-x-1 transition-transform shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* STAY CONNECTED */}
        <section className="py-16 border-t border-border bg-navy-deep">
          <div className="container max-w-2xl">
            <h2 className="font-display uppercase text-white text-2xl md:text-3xl text-center mb-6">
              Stay connected as we build <span className="text-gradient-gold">MAKE CHANGE systems</span>
            </h2>
            <LeadMagnetForm sourcePage="app" />
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default AppHome;
